<script setup lang="ts">
import '../../../../assets/brands/lecker/components/cookbook.scss'

import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, onMounted, ref } from 'vue'

import { registerComponentStyle } from '../../../../components/component-styles'
import { usePageStore } from '../../../../stores/page'
import { useSessionStore } from '../../../../stores/session'
import { useConfigStore } from '../../../../stores/config'
import { useUrlHelper } from '../../../../composables/use-url-helper'
import { getAxios } from '../../../../api/axios'
import { useAssetCollections } from '../../composables/use-asset-collections'
import PinterestShareButton from '../../../../components/shared/PinterestShareButton.vue'

import type { AssetCollectionToggledPayload } from '../../composables/use-asset-collections'

if (import.meta.env.SSR) {
  registerComponentStyle(
    'apps/lecker/theme/Cookbook/ButtonBar.vue',
    'assets/brands/lecker/components/cookbook.scss'
  )
}

const AssetCollectionsDialog = defineAsyncComponent(() => import('./AssetCollectionsDialog.vue'))

const pageStore = usePageStore()
const { pageData, brandFromStore } = storeToRefs(pageStore)
const { isLoggedIn } = storeToRefs(useSessionStore())
const { rsConfig } = storeToRefs(useConfigStore())
const { getQueryParams } = useUrlHelper()
const { assetCollections, getAssetCollections } = useAssetCollections()

const collectionsCount = ref(0)
const showAssetToCollectionsDialog = ref(false)

const assetInCollections = computed(() => assetCollections.value.length)

const addAssetToCollections = () => {
  if (!isLoggedIn.value) {
    window.location.href = `/anmelden?rts=${encodeURIComponent(JSON.stringify({ action: 'addToCookbook' }))}`
  } else {
    showAssetToCollectionsDialog.value = true
  }
}

const assetCollectionToggled = ({ action, collectionId }: AssetCollectionToggledPayload) => {
  if (action === 'remove') {
    assetCollections.value = assetCollections.value.filter(val => val !== collectionId)
    collectionsCount.value--
  } else {
    assetCollections.value.push(collectionId)
    collectionsCount.value++
  }
}

const showPrintPreview = () => {
  document.body.classList.add('bx-page--print')
  scroll(0, 0)

  if (!import.meta.env.SSR && process.env.NODE_ENV !== 'development') {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'custom_tracking_print_button'
    })
  }

  pageStore.setPrintPreview(true)
}

onMounted(async () => {
  const assetId = pageData.value?.pageMeta?.id
  const axios = await getAxios()
  const response = await axios.get(
    `${rsConfig.value.orchestrationServiceUrl}/collections/${assetId}/count?brand=${brandFromStore.value}`
  )
  collectionsCount.value = response?.data?.total || 0
  if (isLoggedIn.value && assetId) {
    await getAssetCollections(assetId)
    if (getQueryParams()?.action === 'addToCookbook') {
      showAssetToCollectionsDialog.value = true
    }
  }
})
</script>

<template>
  <div class="bx-content bx-social-share bx-typo--social-share">
    <div class="bx-social-share__element-list">
      <div class="bx-social-share__element--print">
        <div class="icon icon--social-share icon-print"
             tabindex="0"
             @click="showPrintPreview()">
          <svg><use href="#print" /></svg>
          <span class="bx-social-share__text">
            Drucken
          </span>
        </div>
      </div>

      <PinterestShareButton />

      <div class="bx-social-share__element--cookbook">
        <button class="icon icon--social-share icon-heart icon-heart--filled"
                data-tc="cookbook-button-add"
                @click.stop="addAssetToCollections()">
          <svg><use :href="`#heart${!assetInCollections ? '-outlined' : ''}`" /></svg>
          <span>
            {{ assetInCollections ? 'im' : 'ins' }} Kochbuch
          </span>
          <span class="bx-social-share__text">
            {{ collectionsCount }}
          </span>
        </button>
      </div>
    </div>
    <AssetCollectionsDialog v-if="showAssetToCollectionsDialog && pageData?.pageMeta?.id"
                            class="bx-cookbook"
                            asset-dialog-context="article"
                            :asset-collections-prefetch="assetCollections"
                            :dialog-asset="{
                              assetId: pageData.pageMeta.id,
                              imageId: pageData.metaData?.ogImage?.imageId || null
                            }"
                            @close:dialog="showAssetToCollectionsDialog = false"
                            @asset:collection:toggled="assetCollectionToggled($event)" />
  </div>
</template>
